import apiClient from '../../../../utils/api'
import { RecurrenceRate } from '../../../../utils/enums'
import { occurrencesPerWeekForRate } from '../../repeatsRules'
import { capacityFromLabel } from './propertyOptions'

export type TPlanningCommitmentSource = 'recurring' | 'calendar'

export interface TPlanningCommitment {
    id: string
    source: TPlanningCommitmentSource
    title: string
    minutesPerWeek: number
    occurrencesPerWeek: number
    movesGoal: boolean
    reused: boolean
    confirmed: boolean
    included: boolean
}

export interface TGoalStartingPoint {
    baseline: string
    commitments: TPlanningCommitment[]
    /** UI-only state; omitted from the planner payload. */
    contextLoadFailed?: boolean
}

export type TPlanningCommitmentStatus = 'reused' | 'counted' | 'not_added'

export type TPlanningReviewMode = 'creation' | 'redraft'

export interface TPlanningCommitmentReview {
    sourceLabel: 'Recurring task' | 'Calendar event'
    inclusionLabel: 'Included in planning' | 'Not used in planning'
    relationLabel: 'Moves this goal' | 'Other commitment'
    changeLabel: 'Calendar unchanged' | 'Will link' | 'Already linked' | null
}

export const planningCommitmentStatus = (
    commitment: Pick<TPlanningCommitment, 'included' | 'reused'>,
    reused = commitment.reused
): TPlanningCommitmentStatus => {
    if (reused) return 'reused'
    return commitment.included ? 'counted' : 'not_added'
}

export const planningCommitmentReview = (
    commitment: Pick<TPlanningCommitment, 'source' | 'included' | 'movesGoal' | 'reused'>,
    mode: TPlanningReviewMode,
    reused = commitment.reused
): TPlanningCommitmentReview => ({
    sourceLabel: commitment.source === 'calendar' ? 'Calendar event' : 'Recurring task',
    inclusionLabel: commitment.included ? 'Included in planning' : 'Not used in planning',
    relationLabel: commitment.movesGoal ? 'Moves this goal' : 'Other commitment',
    changeLabel:
        commitment.source === 'calendar'
            ? 'Calendar unchanged'
            : reused
            ? mode === 'creation'
                ? 'Will link'
                : 'Already linked'
            : null,
})

/** Creation can hook a recurring template, so its current choices own `reused` outright.
 *  Do not preserve an earlier derived value when the user goes Back and changes those choices. */
export const creationCommitmentsWithReuse = (commitments: TPlanningCommitment[]): TPlanningCommitment[] =>
    commitments.map((commitment) => ({
        ...commitment,
        reused: commitment.source === 'recurring' && commitment.movesGoal && commitment.included,
    }))

export const newWorkCapacityMinutes = (capacityLabel: string, commitments: TPlanningCommitment[]) => {
    const picked = capacityFromLabel(capacityLabel)
    if (picked?.unit !== 'hrs') return null
    const committedMinutes = commitments
        .filter((commitment) => commitment.included && commitment.movesGoal)
        .reduce((total, commitment) => total + Math.max(0, commitment.minutesPerWeek), 0)
    return Math.max(0, picked.value * 60 - committedMinutes)
}

export const hasCapacityForGoal = (capacityLabel: string, commitments: TPlanningCommitment[]) => {
    const remaining = newWorkCapacityMinutes(capacityLabel, commitments)
    if (remaining == null || remaining >= 30) return true
    // A saved goal can already have all of its stated capacity represented by linked recurring
    // work. That leaves no room for *additional* work, but the existing routine is itself a valid
    // plan to redraft around and must not be unchecked or misclassified just to continue.
    return commitments.some((commitment) => commitment.included && commitment.movesGoal && commitment.reused)
}

interface TPlanningContextResponse {
    /** Older servers may still include this retired field. */
    planning_week_minutes?: number
    calendar_status: 'ready' | 'partial' | 'unavailable' | 'excluded' | 'none'
    calendar_issues: string[]
    commitments: Array<{
        id: string
        source: TPlanningCommitmentSource
        title: string
        minutes_per_week: number
        occurrences_per_week: number
        recurrence_rate?: RecurrenceRate
        block_duration_minutes?: number
        moves_goal: boolean
        reused: boolean
        confirmed: boolean
    }>
}

export interface TPlanningContext {
    calendarStatus: TPlanningContextResponse['calendar_status']
    calendarIssues: string[]
    commitments: TPlanningCommitment[]
}

export const loadPlanningContext = async (
    title: string,
    goalId: string | undefined,
    calendars: Array<{ account_id: string; calendar_ids: string[] }>,
    signal?: AbortSignal
): Promise<TPlanningContext> => {
    const response = await apiClient.post<TPlanningContextResponse>(
        '/goals/planning_context/',
        { title, goal_id: goalId ?? '', calendars },
        { signal }
    )
    return {
        calendarStatus: response.data.calendar_status,
        calendarIssues: response.data.calendar_issues ?? [],
        commitments: (response.data.commitments ?? []).map((commitment) => {
            const occurrences =
                commitment.source === 'recurring' && commitment.recurrence_rate != null
                    ? occurrencesPerWeekForRate(commitment.recurrence_rate)
                    : commitment.occurrences_per_week
            return {
                id: commitment.id,
                source: commitment.source,
                title: commitment.title,
                minutesPerWeek:
                    commitment.source === 'recurring' && commitment.recurrence_rate != null
                        ? occurrences * Math.max(0, commitment.block_duration_minutes ?? 0)
                        : commitment.minutes_per_week,
                occurrencesPerWeek: occurrences,
                movesGoal: commitment.moves_goal,
                reused: commitment.reused,
                confirmed: commitment.confirmed,
                included: true,
            }
        }),
    }
}

export const startingPointForDraft = (startingPoint: TGoalStartingPoint) => ({
    baseline: startingPoint.baseline,
    commitments: startingPoint.commitments.map((commitment) => ({
        title: commitment.title,
        source: commitment.source,
        minutesPerWeek: commitment.minutesPerWeek,
        movesGoal: commitment.movesGoal,
        included: commitment.included,
        status: planningCommitmentStatus(commitment),
    })),
})

export const formatWeeklyTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const remainder = minutes % 60
    if (hours === 0) return `${remainder}m`
    if (remainder === 0) return `${hours}h`
    return `${hours}h ${remainder}m`
}
